import React, { useContext, useState, useEffect } from "react";
import axios from "axios";

import {
  Combobox,
  DataTable,
  DataTableColumn,
  DataTableCell,
  ExpandableSection,
} from "@salesforce/design-system-react";

import PanelGroup from "./PanelGroup";

import { AppContext } from "../context/appContext";

const SelectDataTableCell = ({ children, ...props }) => {
  const initialState = props.dropdownOptions.filter(
    (item) => parseInt(item.id) === parseInt(props.item[props.property])
  );
  const [selection, setSelection] = useState(initialState);

  return (
    <DataTableCell {...props}>
      <Combobox
        id={`combobox-${props.property}-${props.rowId}`}
        events={{
          onSelect: (event, data) => {
            if (data.selection === selection) {
              return;
            }
            props.onChange(props.item.id, data.selection[0].id, props.property);
            setSelection(data.selection);
          },
        }}
        options={props.dropdownOptions}
        selection={selection}
        variant="readonly"
      />
    </DataTableCell>
  );
};
SelectDataTableCell.displayName = DataTableCell.displayName;

const RemoveDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell {...props}>
      <a
        href={children}
        onClick={(event) => {
          event.preventDefault();
          props.onRemove(props.item.id);
        }}
      >
        Remove
      </a>
    </DataTableCell>
  );
};
RemoveDataTableCell.displayName = DataTableCell.displayName;

const MappingGroupSection = ({ salesforceObjectId }) => {
  const [salesforceObjectName, setSalesforceObjectName] = useState("");
  const [salesforceFields, setSalesforceFields] = useState([]);
  const [hootsuiteFields, setHootsuiteFields] = useState([]);
  const [pluginMappings, setPluginMappings] = useState([]);

  const { jwt } = useContext(AppContext);

  useEffect(() => {
    const fetchSalesforceObject = async () => {
      try {
        const result = await axios({
          method: "get",
          url: `/api/salesforce_objects/${salesforceObjectId}`,
          params: {
            jwt: jwt,
          },
        });
        console.log(`url: /api/salesforce_objects/${salesforceObjectId}`);
        console.log(result);
        setSalesforceObjectName(result.data.label);
        setSalesforceFields(result.data.salesforceFields);
        setHootsuiteFields(result.data.hootsuiteFields);
        setPluginMappings(result.data.pluginMappings);
      } catch (error) {
        console.log(error);
      }
    };
    fetchSalesforceObject();
  }, [salesforceObjectId]);

  const columns = [
    <DataTableColumn
      key="hs-field-id"
      label="Hootsuite Object Fieldname"
      property="hootsuiteFieldId"
    >
      <SelectDataTableCell
        dropdownOptions={hootsuiteFields}
        onChange={(pluginMappingId, selectedFieldId, selectedFieldType) =>
          handleFieldSelect(pluginMappingId, selectedFieldId, selectedFieldType)
        }
        rowId={salesforceObjectId}
      />
    </DataTableColumn>,
    <DataTableColumn
      key="sf-field-id"
      label="Salesforce Object Fieldname"
      property="salesforceFieldId"
    >
      <SelectDataTableCell
        dropdownOptions={salesforceFields}
        onChange={(pluginMappingId, selectedFieldId, selectedFieldType) =>
          handleFieldSelect(pluginMappingId, selectedFieldId, selectedFieldType)
        }
        rowId={salesforceObjectId}
      />
    </DataTableColumn>,
    <DataTableColumn key="plugin-mapping-id" label="Action" property="id">
      <RemoveDataTableCell onRemove={(id) => handleRemovePluginMapping(id)} />
    </DataTableColumn>,
  ];

  const handleFieldSelect = (
    pluginMappingId,
    selectedFieldId,
    selectedFieldType
  ) => {
    const newPluginMappings = pluginMappings.map((item) => {
      if (item.id === pluginMappingId) {
        const updatedItem = { ...item };
        updatedItem[selectedFieldType] = selectedFieldId;
        return updatedItem;
      }
      return item;
    });

    const pluginMapping = newPluginMappings.filter(
      (item) => item.id === pluginMappingId
    )[0];
    if (
      pluginMapping.hasOwnProperty("hootsuiteFieldId") &&
      pluginMapping.hasOwnProperty("salesforceFieldId")
    ) {
      if (pluginMappingId <= 0) {
        // call create api if both values exist
        const createPluginMapping = async () => {
          try {
            const result = await axios({
              method: "post",
              url: `/api/plugin_mappings`,
              data: {
                jwt: jwt,
                hootsuiteFieldId: parseInt(pluginMapping.hootsuiteFieldId),
                salesforceFieldId: parseInt(pluginMapping.salesforceFieldId),
                salesforceObjectId: parseInt(salesforceObjectId),
              },
            });
            console.log(`url: /api/plugin_mappings`);
            console.log(result);

            const newPluginMappingsUpdatedId = newPluginMappings.map((item) => {
              if (item.id === pluginMappingId) {
                const updatedItem = { ...item };
                updatedItem["id"] = result.data.id;
                return updatedItem;
              }
              return item;
            });
            // update object with validation message or errors
            setPluginMappings(newPluginMappingsUpdatedId);
          } catch (error) {
            console.log(error);
          }
        };
        createPluginMapping();
      } else {
        // call update api
        const updatePluginMapping = async () => {
          try {
            const result = await axios({
              method: "patch",
              url: `/api/plugin_mappings/${pluginMappingId}`,
              data: {
                jwt: jwt,
                hootsuiteFieldId: parseInt(pluginMapping.hootsuiteFieldId),
                salesforceFieldId: parseInt(pluginMapping.salesforceFieldId),
              },
            });
            console.log(`url: /api/plugin_mappings/${pluginMappingId}`);
            console.log(result);
            // update object with validation message or errors
            setPluginMappings(newPluginMappings);
          } catch (error) {
            console.log(error);
          }
        };
        updatePluginMapping();
      }
    } else {
      {
        console.log("clientside validation need both values");
        setPluginMappings(newPluginMappings);
        return;
      }
    }
  };

  const handleRemovePluginMapping = (pluginMappingId) => {
    console.log("REMOVE ROW", pluginMappingId);
    const newPluginMappings = pluginMappings.filter(
      (item) => item.id !== pluginMappingId
    );
    setPluginMappings(newPluginMappings);
    if (pluginMappingId > 0) {
      const destroyPluginMapping = async () => {
        try {
          const result = await axios({
            method: "delete",
            url: `/api/plugin_mappings/${pluginMappingId}`,
            data: {
              jwt: jwt,
            },
          });
          console.log(`url: /api/plugin_mappings/${pluginMappingId}`);
          console.log(result);
        } catch (error) {
          console.log(error);
        }
      };
      destroyPluginMapping();
    }
  };

  const handleAdd = () => {
    console.log("HANDLE ADD");
    setPluginMappings((prevPluginMappings) => [
      ...prevPluginMappings,
      { id: `-${Date.now()}` },
    ]);
  };

  return (
    <ExpandableSection
      id={`salesforce-object-mapping-section-${salesforceObjectId}`}
      title={salesforceObjectName}
      key={salesforceObjectId}
    >
      <PanelGroup addLabel="Add Mapping" onAdd={() => handleAdd()}>
        <DataTable
          items={pluginMappings}
          id={`plugin-mappings-table-${salesforceObjectId}`}
        >
          {columns}
        </DataTable>
      </PanelGroup>
    </ExpandableSection>
  );
};

export default MappingGroupSection;
